import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconoChevronAbajo, IconoBuscar, IconoCheck } from "../../UI/Iconos";
import { inicialesEntidad } from "../../../utils/tarifarios";

/* ============================================================================
   TARIFARIOS-UI FASE 1 · selector de entidad

   POR QUÉ NO ES EL `SelectSearchble` DE SIEMPRE: este control decide TODOS los
   precios de la tabla. Es el error más caro de la pantalla -- editar el
   tarifario de otra aseguradora por no fijarse en un `<select>` de 240px en la
   esquina. Un react-select estándar no puede pintar el avatar de iniciales, ni
   el borde primario, ni verse como el control dominante que es.

   Es un `<button>` que abre un panel con buscador, no un `<select>`: teclado
   completo (Enter/Espacio abre, flechas recorren, Enter elige, Esc cierra y
   devuelve el foco), y el nombre de la entidad se lee sin desplegar nada.

   No pide datos: recibe la lista ya cargada por la pantalla. Cambiar de
   entidad recarga la TABLA, no la pantalla (Trampa 4) -- eso lo garantiza el
   padre, que sólo cambia un estado.
   ========================================================================== */

function Avatar({ nombre, tamano = 26 }) {
    return (
        <span
            className="tar-avatar"
            style={{ width: tamano, height: tamano }}
            aria-hidden="true"
        >
            {inicialesEntidad(nombre)}
        </span>
    );
}

export default function SelectorEntidad({
    entidades = [],
    seleccionada,
    onCambio,
    disabled = false,
    // El buscador interno sólo aparece cuando la lista es larga de verdad. Con
    // las 19 aseguradoras reales de producción caben todas de un vistazo, y un
    // campo de búsqueda para 19 filas es un obstáculo, no una ayuda.
    umbralBuscador = 12,
}) {
    const [abierto, setAbierto] = useState(false);
    const [filtro, setFiltro] = useState("");
    const [resaltada, setResaltada] = useState(0);
    const cajaRef = useRef(null);
    const disparadorRef = useRef(null);
    const buscadorRef = useRef(null);

    const visibles = useMemo(() => {
        const texto = filtro.trim().toLocaleLowerCase("es");
        if (!texto) return entidades;
        return entidades.filter((e) => String(e.label).toLocaleLowerCase("es").includes(texto));
    }, [entidades, filtro]);

    const cerrar = useCallback(
        (devolverFoco = true) => {
            setAbierto(false);
            setFiltro("");
            // El foco vuelve al disparador: sin esto, cerrar con Esc deja el
            // foco en el <body> y el siguiente Tab arranca desde el principio
            // de la página.
            if (devolverFoco) disparadorRef.current?.focus();
        },
        []
    );

    const elegir = useCallback(
        (entidad) => {
            cerrar();
            // Reelegir la misma entidad no dispara nada: si no, cada clic en
            // el nombre que ya está puesto sería otra consulta al servidor.
            if (entidad && entidad.value !== seleccionada?.value) onCambio?.(entidad);
        },
        [cerrar, onCambio, seleccionada]
    );

    /* Clic fuera. Se escucha en `mousedown` y no en `click` para que cerrar el
       panel no dispare además el control que hay debajo. */
    useEffect(() => {
        if (!abierto) return undefined;
        const alPulsarFuera = (evento) => {
            if (!cajaRef.current?.contains(evento.target)) cerrar(false);
        };
        document.addEventListener("mousedown", alPulsarFuera);
        return () => document.removeEventListener("mousedown", alPulsarFuera);
    }, [abierto, cerrar]);

    /* Al abrir: el foco va al buscador si lo hay, y la entidad actual queda
       resaltada -- abrir y pulsar Enter sin mirar no debe cambiar nada. */
    useEffect(() => {
        if (!abierto) return;
        const actual = entidades.findIndex((e) => e.value === seleccionada?.value);
        setResaltada(actual >= 0 ? actual : 0);
        if (entidades.length >= umbralBuscador) buscadorRef.current?.focus();
    }, [abierto, entidades, seleccionada, umbralBuscador]);

    const alTeclear = (evento) => {
        if (evento.key === "Escape") {
            evento.preventDefault();
            cerrar();
            return;
        }
        if (evento.key === "ArrowDown" || evento.key === "ArrowUp") {
            evento.preventDefault();
            if (visibles.length === 0) return;
            setResaltada((previa) => {
                const paso = evento.key === "ArrowDown" ? 1 : -1;
                return (previa + paso + visibles.length) % visibles.length;
            });
            return;
        }
        if (evento.key === "Enter") {
            evento.preventDefault();
            elegir(visibles[resaltada]);
        }
    };

    const conBuscador = entidades.length >= umbralBuscador;
    const idPanel = "tar-entidad-panel";

    return (
        <div className="tar-entidad" ref={cajaRef} data-testid="tarifario-entidad">
            <span className="tar-entidad__etiqueta" id="tar-entidad-etiqueta">
                Entidad / seguro
            </span>

            <button
                type="button"
                ref={disparadorRef}
                className="tar-entidad__disparador"
                onClick={() => (abierto ? cerrar() : setAbierto(true))}
                onKeyDown={(e) => {
                    // Abrir con teclado y caer directamente en la lista.
                    if (!abierto && (e.key === "ArrowDown" || e.key === "Enter")) {
                        e.preventDefault();
                        setAbierto(true);
                    }
                }}
                disabled={disabled || entidades.length === 0}
                aria-haspopup="listbox"
                aria-expanded={abierto}
                aria-controls={abierto ? idPanel : undefined}
                aria-labelledby="tar-entidad-etiqueta"
                data-testid="tarifario-entidad-disparador"
            >
                {seleccionada ? (
                    <>
                        <Avatar nombre={seleccionada.label} />
                        <span className="tar-entidad__nombre">{seleccionada.label}</span>
                    </>
                ) : (
                    <span className="tar-entidad__vacio">
                        {entidades.length === 0 ? "Sin entidades" : "Seleccione una entidad"}
                    </span>
                )}
                <IconoChevronAbajo size={17} className="tar-entidad__chevron" />
            </button>

            {abierto && (
                <div className="tar-entidad__panel" onKeyDown={alTeclear}>
                    {conBuscador && (
                        <div className="tar-entidad__buscador">
                            <IconoBuscar size={15} />
                            <input
                                ref={buscadorRef}
                                type="text"
                                value={filtro}
                                onChange={(e) => {
                                    setFiltro(e.target.value);
                                    setResaltada(0);
                                }}
                                placeholder="Buscar entidad"
                                aria-label="Buscar entidad"
                                data-testid="tarifario-entidad-buscar"
                            />
                        </div>
                    )}

                    <ul
                        className="tar-entidad__lista"
                        id={idPanel}
                        role="listbox"
                        aria-labelledby="tar-entidad-etiqueta"
                        tabIndex={conBuscador ? -1 : 0}
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus={!conBuscador}
                    >
                        {visibles.length === 0 && (
                            <li className="tar-entidad__sinresultados">Ninguna entidad coincide.</li>
                        )}
                        {visibles.map((entidad, i) => {
                            const activa = entidad.value === seleccionada?.value;
                            return (
                                <li key={entidad.value}>
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={activa}
                                        className={`tar-entidad__opcion${
                                            i === resaltada ? " is-resaltada" : ""
                                        }${activa ? " is-activa" : ""}`}
                                        onMouseEnter={() => setResaltada(i)}
                                        onClick={() => elegir(entidad)}
                                    >
                                        <Avatar nombre={entidad.label} tamano={24} />
                                        <span className="tar-entidad__opcion-nombre">{entidad.label}</span>
                                        {activa && <IconoCheck size={15} />}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}

export { Avatar };
